/**
 * The address a stored document is loaded from.
 *
 * Records hold a path in one of four shapes, depending on when and by which
 * controller they were written:
 *
 *   app/public/proposals/proposal_123.pdf
 *   storage/app/public/proposals/proposal_123.pdf
 *   /storage/proposals/proposal_123.pdf
 *   proposals/proposal_123.pdf
 *
 * All of them become one address on the host this link belongs to. It used to
 * rewrite only the first shape, and only to a hardcoded api.cleancinch.com — so a
 * staging link fetched live customers' documents, and any other shape produced an
 * address that could not resolve, which is how a proposal link lands on a blank
 * viewer instead of a document.
 *
 * Mirrors StoredFile::url() on the backend and fileUrl() in the web app, so the
 * three agree on one address per stored path.
 */

import { apiRoot } from "../config/apiBase";

/** Hides the browser's own PDF chrome, so the page frames the document itself. */
const VIEWER_PARAMS = "#toolbar=0&navpanes=0&scrollbar=0&view=FitH";

const storagePath = (stored: string): string => {
  let path = String(stored).trim();

  // An address that already carries a host: keep what follows /storage/ or /files/
  const marker = path.match(/\/(?:storage|files)\/(.+)$/);
  if (marker) return marker[1];

  path = path.replace(/^\/+/, "");

  for (const prefix of ["app/public/", "storage/app/public/", "storage/"]) {
    if (path.startsWith(prefix)) return path.slice(prefix.length);
  }

  return path;
};

const transformPDFUrl = (url: string): string => {
  if (!url) return "";

  // Already absolute and ours — only the viewer parameters are needed
  const relative = storagePath(url);
  if (!relative) return "";

  return `${apiRoot()}/files/${relative}${VIEWER_PARAMS}`;
};

export { transformPDFUrl };
